import { useEffect, useRef, useState, type MouseEvent, type RefObject } from "react";
import type { NodeDto, NodeRelationshipDto, WritingDto } from "@mmm/contracts";
import type { NodeSize } from "./MapEditingContext.js";
import { NodeCard } from "./NodeCard.js";
import { RelationshipEdgeSvg, type DisplayRelationship } from "./RelationshipEdgeSvg.js";

interface Point {
  x: number;
  y: number;
}

type CanvasNode = NodeDto & { writing?: Pick<WritingDto, "markdown"> };

interface DragState {
  nodeId: string;
  offsetX: number;
  offsetY: number;
}

interface PanState {
  startX: number;
  startY: number;
  panX: number;
  panY: number;
}

interface CanvasProps {
  /** Owned by MapEditor, which measures it to fit the view to the content. */
  boardRef: RefObject<HTMLDivElement | null>;
  nodes: CanvasNode[];
  nodeSizePreview: (NodeSize & { nodeId: string }) | null;
  relationships: DisplayRelationship[];
  pan: Point;
  zoom: number;
  gridSize: number;
  onPanChange: (pan: Point) => void;
  selectedNodeId: string | null;
  connectMode: boolean;
  connectFromId: string | null;
  onNodeClick: (node: NodeDto) => void;
  onBlankClick: () => void;
  onNodeRename: (node: NodeDto, title: string) => void;
  /** May return the save's promise so a failed move can be reverted below —
   * returning nothing is also fine (nothing to revert). */
  onNodeMove: (nodeId: string, x: number, y: number) => Promise<unknown> | void;
  onRelationshipDelete: (relationship: NodeRelationshipDto) => void;
}

export function Canvas({
  boardRef,
  nodes,
  nodeSizePreview,
  relationships,
  pan,
  zoom,
  gridSize,
  onPanChange,
  selectedNodeId,
  connectMode,
  connectFromId,
  onNodeClick,
  onBlankClick,
  onNodeRename,
  onNodeMove,
  onRelationshipDelete,
}: CanvasProps) {
  const dragState = useRef<DragState | null>(null);
  const panState = useRef<PanState | null>(null);
  const liveDragPosition = useRef<Point | null>(null);
  const snapModifier = useRef(false);
  const [snapActive, setSnapActive] = useState(false);
  const [connectPointer, setConnectPointer] = useState<Point | null>(null);
  // Local drag positions overlay the persisted ones — and deliberately stay
  // after mouseup: the persisted position only catches up after the
  // updateNode round-trip, so clearing on release would flash the node back
  // to its old spot for a beat. Once the server data matches, the overlay is
  // a no-op; the next drag of the same node overwrites it.
  const [dragPositions, setDragPositions] = useState<Record<string, Point>>({});

  const renderNodes = nodes.map((node) => {
    const dragged = dragPositions[node.id];
    const resized = nodeSizePreview?.nodeId === node.id ? nodeSizePreview : null;
    return {
      ...node,
      ...(dragged ? { x: dragged.x, y: dragged.y } : {}),
      ...(resized ? { width: resized.width, height: resized.height } : {}),
    };
  });
  // Edges are drawn from the same overlaid positions so they follow the node
  // during a drag instead of lagging at the persisted position.
  const nodesById = new Map(renderNodes.map((node) => [node.id, node]));
  const connectSourceNode = connectFromId ? nodesById.get(connectFromId) : null;

  useEffect(() => {
    const syncSnap = (event: KeyboardEvent) => {
      const active = event.ctrlKey || event.metaKey;
      snapModifier.current = active;
      setSnapActive(active);
    };
    const clearSnap = () => {
      snapModifier.current = false;
      setSnapActive(false);
    };

    window.addEventListener("keydown", syncSnap);
    window.addEventListener("keyup", syncSnap);
    window.addEventListener("blur", clearSnap);
    return () => {
      window.removeEventListener("keydown", syncSnap);
      window.removeEventListener("keyup", syncSnap);
      window.removeEventListener("blur", clearSnap);
    };
  }, []);

  const toCanvasPoint = (clientX: number, clientY: number): Point => {
    const rect = boardRef.current!.getBoundingClientRect();
    return { x: (clientX - rect.left - pan.x) / zoom, y: (clientY - rect.top - pan.y) / zoom };
  };

  const snapPoint = (point: Point): Point => ({
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize,
  });

  const shouldSnap = (modifierActive: boolean) => modifierActive || snapModifier.current;

  const moveInteraction = (clientX: number, clientY: number, modifierActive: boolean) => {
    const snapping = shouldSnap(modifierActive);
    setSnapActive(snapping);

    if (dragState.current) {
      const point = toCanvasPoint(clientX, clientY);
      const { nodeId, offsetX, offsetY } = dragState.current;
      const next = { x: point.x - offsetX, y: point.y - offsetY };
      const position = snapping ? snapPoint(next) : next;
      liveDragPosition.current = position;
      setDragPositions((prev) => ({
        ...prev,
        [nodeId]: position,
      }));
    } else if (panState.current) {
      onPanChange({
        x: panState.current.panX + (clientX - panState.current.startX),
        y: panState.current.panY + (clientY - panState.current.startY),
      });
    }
  };

  /** Drops the local drag overlay for one node, letting it fall back to the
   * `nodes` prop's (server-truth) position — used when a move fails to save,
   * so a rejected `updateNode` doesn't leave the node stuck at a position
   * the server never persisted. */
  const revertDragPosition = (nodeId: string) => {
    setDragPositions((prev) => {
      if (!(nodeId in prev)) return prev;
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });
  };

  const finishInteraction = (modifierActive: boolean) => {
    if (dragState.current) {
      const { nodeId } = dragState.current;
      const position = liveDragPosition.current ?? dragPositions[nodeId];
      const next = position && shouldSnap(modifierActive) ? snapPoint(position) : position;
      if (next) {
        setDragPositions((prev) => ({ ...prev, [nodeId]: next }));
        const result = onNodeMove(nodeId, next.x, next.y);
        result?.catch(() => revertDragPosition(nodeId));
      }
      dragState.current = null;
      liveDragPosition.current = null;
      // dragPositions intentionally kept on success — see comment at its
      // declaration. On failure, revertDragPosition (above) clears it.
    }
    panState.current = null;
  };

  useEffect(() => {
    const handleWindowMouseMove = (event: globalThis.MouseEvent) => {
      if (!dragState.current && !panState.current) return;
      event.preventDefault();
      moveInteraction(event.clientX, event.clientY, event.ctrlKey || event.metaKey);
    };

    const handleWindowMouseUp = (event: globalThis.MouseEvent) => {
      if (!dragState.current && !panState.current) return;
      finishInteraction(event.ctrlKey || event.metaKey);
    };

    const clearInteraction = () => {
      finishInteraction(false);
      snapModifier.current = false;
      setSnapActive(false);
    };

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);
    window.addEventListener("blur", clearInteraction);
    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove);
      window.removeEventListener("mouseup", handleWindowMouseUp);
      window.removeEventListener("blur", clearInteraction);
    };
  });

  const handleNodeMouseDown = (event: MouseEvent, node: NodeDto) => {
    event.stopPropagation();
    if (connectMode) {
      setConnectPointer({ x: node.x + node.width / 2, y: node.y + node.height / 2 });
      return;
    }
    const modifierActive = event.ctrlKey || event.metaKey;
    snapModifier.current = modifierActive;
    setSnapActive(modifierActive);
    const point = toCanvasPoint(event.clientX, event.clientY);
    dragState.current = { nodeId: node.id, offsetX: point.x - node.x, offsetY: point.y - node.y };
    liveDragPosition.current = { x: node.x, y: node.y };
  };

  const handleBackgroundMouseDown = (event: MouseEvent) => {
    if (connectMode) return;
    panState.current = { startX: event.clientX, startY: event.clientY, panX: pan.x, panY: pan.y };
  };

  const handleCanvasMouseMove = (event: MouseEvent) => {
    if (!connectFromId || dragState.current || panState.current) return;
    setConnectPointer(toCanvasPoint(event.clientX, event.clientY));
  };

  return (
    <div
      ref={boardRef}
      className={`mmm-map-canvas ${snapActive ? "is-snap-active" : ""} ${connectMode ? "is-connect-mode" : ""}`.trim()}
      style={{
        backgroundPosition: `${pan.x}px ${pan.y}px`,
        backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px`,
      }}
      onMouseDown={handleBackgroundMouseDown}
      onClick={() => {
        if (!connectMode) onBlankClick();
      }}
      onMouseMove={handleCanvasMouseMove}
      onMouseLeave={() => setConnectPointer(null)}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: "0 0",
        }}
      >
        <RelationshipEdgeSvg nodesById={nodesById} relationships={relationships} onDeleteRelationship={onRelationshipDelete} />
        {connectSourceNode && connectPointer && (
          <svg className="mmm-map-connect-preview" aria-hidden="true">
            <path
              d={`M ${connectSourceNode.x + connectSourceNode.width / 2} ${connectSourceNode.y + connectSourceNode.height / 2} L ${connectPointer.x} ${connectPointer.y}`}
            />
          </svg>
        )}
        {renderNodes.map((node) => (
          <NodeCard
            key={node.id}
            node={node}
            hasWriting={Boolean(node.writing?.markdown.trim())}
            selected={node.id === selectedNodeId}
            connectSource={node.id === connectFromId}
            connectMode={connectMode}
            onMouseDown={(event) => handleNodeMouseDown(event, node)}
            onClick={(event) => {
              event.stopPropagation();
              onNodeClick(node);
            }}
            onRename={(title) => onNodeRename(node, title)}
          />
        ))}
        <RelationshipEdgeSvg nodesById={nodesById} relationships={relationships} onDeleteRelationship={onRelationshipDelete} controlsOnly />
      </div>
    </div>
  );
}
